import { useState } from 'react'
import type { Recipe, ShoppingItem } from '../types'
import { formatIngredient, shoppingItemFromIngredient } from '../utils/format'
import { heroImageSrc } from '../utils/images'
import { SpecialDoughCalculator } from './SpecialDoughCalculator'

type RecipePageProps = {
  recipe: Recipe
  onBack: () => void
  onAdd: (items: ShoppingItem[]) => void
}

export function RecipePage({ recipe, onBack, onAdd }: RecipePageProps) {
  const [scaleValue, setScaleValue] = useState(recipe.scaler?.base ?? 1)
  const scaleFactor = recipe.scaler ? scaleValue / recipe.scaler.base : 1
  // R5 removed the "✦" filler card from the aside. Without it, a recipe with neither a
  // warning nor a note has nothing to put there — collapse the grid to one column instead
  // of leaving an empty gutter (this also settles M6: no more orphaned card at the bottom).
  const hasAside = Boolean(recipe.warning || recipe.note)

  const addIngredient = (ingredient: Recipe['sections'][number]['items'][number]) => {
    onAdd([shoppingItemFromIngredient(ingredient, recipe.title, scaleFactor)])
  }

  const addAll = () => {
    const items = recipe.sections.flatMap((group) =>
      group.items.map((item) => shoppingItemFromIngredient(item, recipe.title, scaleFactor)),
    )
    onAdd(items)
  }

  return (
    <main className="page-main recipe-page">
      <button className="back-link" onClick={onBack} type="button">
        <span aria-hidden="true">←</span> Toutes les recettes
      </button>
      <article>
        <header className="recipe-hero">
          <div className="recipe-hero-image">
            {/* I2: this is the LCP element on a recipe page — no loading="lazy" here,
                fetchpriority="high" instead so the browser fetches it first. */}
            <img alt="" fetchPriority="high" height={900} src={heroImageSrc(recipe.image)} width={900} />
          </div>
          <div className="recipe-hero-content">
            <div className="tag-row">
              <span className="recipe-category">{recipe.category}</span>
              {recipe.tags.slice(0, 3).map((tag, index) => (
                <span className="tag" key={`${recipe.slug}-tag-${tag}-${index}`}>
                  {tag}
                </span>
              ))}
            </div>
            <h1>{recipe.title}</h1>
            <p className="recipe-description">{recipe.description}</p>
            {recipe.meta.length > 0 && (
              <div className="recipe-meta">
                {recipe.meta.map((item, index) => (
                  <div key={`${recipe.slug}-meta-${item.label}-${index}`}>
                    <strong>{item.label}</strong>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            )}
            {recipe.scaler && <ScaleControl recipe={recipe} value={scaleValue} onChange={setScaleValue} />}
          </div>
        </header>

        {recipe.special ? (
          <SpecialDoughCalculator onAdd={onAdd} recipe={recipe} />
        ) : (
          <div className={`recipe-body${hasAside ? '' : ' recipe-body-single'}`}>
            <div className="recipe-content-column">
              <section className="recipe-section ingredients-section" aria-labelledby="ingredients-title">
                <div className="section-heading">
                  <h2 id="ingredients-title">Ingrédients</h2>
                  <button className="button secondary" onClick={addAll} type="button">
                    Ajouter à la liste de courses
                  </button>
                </div>
                {recipe.sections.map((group, groupIndex) => (
                  <div className="ingredient-group" key={`${recipe.slug}-group-${group.title}-${groupIndex}`}>
                    {/* R1: a group literally titled "Ingrédients" only repeats the h2
                        right above it — hide it, whether it's the recipe's only group
                        (10 recipes) or the main group next to a real one like "Option"
                        (caviar d'aubergines, naans). Real group names (Base / Épices /
                        Option, or the two pâtes-aux-châtaignes yields) stay visible. */}
                    {group.title !== 'Ingrédients' && <h3>{group.title}</h3>}
                    <ul className="ingredient-list">
                      {group.items.map((item, itemIndex) => (
                        <li
                          className={item.optional ? 'is-optional' : ''}
                          key={`${recipe.slug}-ingredient-${groupIndex}-${item.id}-${itemIndex}`}
                        >
                          <span className="ingredient-text">
                            {formatIngredient(item, scaleFactor)}
                            {item.optional && <small>optionnel</small>}
                          </span>
                          <button
                            aria-label={`Ajouter ${item.name} à la liste de courses`}
                            className="add-ingredient"
                            onClick={() => addIngredient(item)}
                            type="button"
                          >
                            +
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
              <section className="recipe-section steps-section" aria-labelledby="steps-title">
                <div className="section-heading">
                  <h2 id="steps-title">Préparation</h2>
                </div>
                <ol className="steps-list">
                  {recipe.steps.map((step, index) => (
                    <li key={`${recipe.slug}-${index}`}>
                      <span className="step-number">{String(index + 1).padStart(2, '0')}</span>
                      <p>{step}</p>
                    </li>
                  ))}
                </ol>
              </section>
            </div>
            {hasAside && (
              <aside className="recipe-aside">
                {recipe.warning && (
                  <div className="recipe-callout warning">
                    <strong>Attention</strong>
                    <p>{recipe.warning}</p>
                  </div>
                )}
                {recipe.note && (
                  <div className="recipe-callout">
                    <strong>Note personnelle</strong>
                    <p>{recipe.note}</p>
                  </div>
                )}
              </aside>
            )}
          </div>
        )}
      </article>
    </main>
  )
}

function ScaleControl({
  recipe,
  value,
  onChange,
}: {
  recipe: Recipe
  value: number
  onChange: (value: number) => void
}) {
  if (!recipe.scaler) return null
  return (
    <div className="scale-control">
      <label htmlFor="recipe-scale">
        <span>
          <strong>{recipe.scaler.label}</strong>
          <output>{recipe.scaler.resultLabel(value)}</output>
        </span>
        <input
          id="recipe-scale"
          max={recipe.scaler.max}
          min={recipe.scaler.min}
          onChange={(event) => onChange(Number(event.target.value))}
          step={recipe.scaler.step}
          type="range"
          value={value}
        />
      </label>
    </div>
  )
}
